<template>
  <div>
    <v-layout class="pt-3 orders-counts-toolbar print-display-none">
      <date-picker
        :selected-date-range="selectedDate"
        label="common.selectedDateRange"
        range
        class="pr-4"
        @onDateUpdate="onDateUpdate"
      />
      <v-autocomplete
        v-model="selectedProviderData"
        :items="filteredProviderData"
        :item-text="i => i.name"
        :item-value="i => ({id: i.id, name: i.name})"
        class="pr-4"
        :label="$t('manageProvidersModal.providerName')"
        :no-data-text="$t('ordersCountContainer.noProvidersHeader')"
      />
      <v-checkbox
        v-model="includeFreeOfCharge"
        color="primary"
        :label="$t('ordersCountContainer.includeFreeOfCharge')"
      />
    </v-layout>
    <dialog-wrapper :visible="dialog">
      <v-dialog
        v-if="selectedProviderToPay"
        v-model="dialog"
        persistent
        max-width="500"
      >
        <v-card>
          <div v-if="selectedProviderToPay.paidStatus">
            <v-card-title class="headline">
              {{ $t('ordersCountContainer.undoPayment') }}
            </v-card-title>
            <v-card-text>
              {{ $t('ordersCountContainer.markProviderNotPaid',
                    {
                      total: selectedProviderToPay.cost.toFixed(2),
                      providerName: selectedProviderToPay.name,
                      date: selectedProviderToPay.date
                    }) }}
            </v-card-text>
          </div>
          <div v-else>
            <v-card-title class="headline">
              {{ $t('ordersCountContainer.confirmPayment') }}
            </v-card-title>
            <v-card-text>
              {{ $t('ordersCountContainer.markProviderPaid',
                    {
                      total: selectedProviderToPay.cost.toFixed(2),
                      providerName: selectedProviderToPay.name,
                      date: selectedProviderToPay.date
                    }) }}
            </v-card-text>
          </div>
          <v-card-actions>
            <v-spacer />
            <v-btn
              flat="flat"
              @click="closeChangeProviderPaidStatusDialog"
            >
              {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              :disabled="loading > 0"
              :loading="loading > 0"
              @click="confirmProviderPaidStatusChange(selectedProviderToPay.id, !selectedProviderToPay.paidStatus)"
            >
              {{ $t('common.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </dialog-wrapper>
    <v-flex
      v-if="selectedProviderData"
      xs12
      lg10
      xl8
      style="margin: auto;"
      class="pb-5 px-2 orders-list-table"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ selectedProviderData.name }}</h3>
          <v-spacer />
          <v-tooltip
            v-if="!includeFreeOfCharge"
            bottom
            :disabled="!dayProvidersFromDateRangeWithPaging.allDayProvidersPaid && isMaxOrderUntilDateInPast"
          >
            <v-layout slot="activator" class="print-display-none">
              <v-checkbox
                v-model="dayProvidersFromDateRangeWithPaging.allDayProvidersPaid"
                :disabled="dayProvidersFromDateRangeWithPaging.allDayProvidersPaid || !isMaxOrderUntilDateInPast"
                hide-details
                class="all-paid-checkbox"
                color="primary"
                :label="$t('ordersCountContainer.markAllDaysAsPaid')"
                @click="bulkMarkAsPaid()"
              />
            </v-layout>
            <span>
              {{ dayProvidersFromDateRangeWithPaging.allDayProvidersPaid
                ? $t('ordersCountContainer.markAllDaysAsPaidAllPaidTooltip')
                : $t('ordersCountContainer.markAllDaysAsPaidStillOrderingTooltip') }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-data-table
          ref="dayProvidersDataTable"
          :no-data-text="$t('dataTables.noData')"
          :rows-per-page-text="$t('dataTables.rowsPerPage')"
          :headers="dayProviderHeaders"
          :items="mappedDayProviders"
          :hide-actions="printing"
          :hide-headers="printing"
          :loading="loading > 0"
          class="elevation-1"
          :rows-per-page-items="rowsPerPage"
          :pagination.sync="dayProvidersPagination"
          :total-items="dayProvidersFromDateRangeWithPaging.pagingData ? dayProvidersFromDateRangeWithPaging.pagingData.totalResultsCount : 0"
        >
          <template slot="items" slot-scope="props">
            <tr @click="props.expanded = !props.expanded; selectProvider(props.item.id)">
              <td class="text-xs-left">
                <strong>{{ `${props.item.date} (${getDayOfWeek(props.item.date)})` }}</strong>
              </td>
              <td class="text-xs-right print-display-none">{{ props.item.cost.toFixed(2) }} &euro;</td>
              <td v-if="!props.item.isFreeOfCharge && props.item.cost > 0" class="text-xs-right print-display-none">
                <v-tooltip
                  v-if="props.item.cost > 0"
                  bottom
                  :disabled="props.item.orderingTimeEnded"
                >
                  <v-layout slot="activator" class="paid-checkbox">
                    <v-checkbox
                      v-model="props.item.paidStatus"
                      color="primary"
                      :disabled="!props.item.orderingTimeEnded"
                      @click.native.stop
                      @click="openChangeProviderPaidStatusDialog(props.item)"
                    />
                  </v-layout>
                  <span v-t="'ordersCountContainer.paidCheckBoxTooltip'" />
                </v-tooltip>
                <div
                  v-if="props.item.paidStatusChangedByName"
                  class="print-display-none grey--text"
                  style="float: right;"
                >
                  <span v-t="'ordersCountContainer.paidStatusChangedBy'" />
                  {{ props.item.paidStatusChangedByName }}
                </div>
              </td>
              <td v-else-if="props.item.isFreeOfCharge" class="text-xs-right print-display-none">
                <v-tooltip bottom>
                  <v-icon slot="activator">credit_card_off</v-icon>
                  <span v-t="'ordersCountContainer.freeOfChargeTooltip'" />
                </v-tooltip>
              </td>
              <td v-else-if="props.item.cost === 0" class="text-xs-right print-display-none">
                <v-tooltip bottom>
                  <v-icon slot="activator">credit_card_off</v-icon>
                  <span v-t="'ordersCountContainer.zeroCostTooltip'" />
                </v-tooltip>
              </td>
            </tr>
          </template>
          <template slot="expand" slot-scope="{}">
            <day-provider-dishes-table
              :day-provider-id="selectedProviderId"
              :printing="printing"
              class="orders-counts-table"
            />
          </template>
          <template slot="footer">
            <td colspan="100%" class="pr-4">
              <div>
                <strong style="float: right;">
                  <span v-t="'ordersCountContainer.total'" /> {{ dayProvidersFromDateRangeWithPaging.totalCostPerDateRange.toFixed(2) }} &euro;
                </strong>
              </div>
            </td>
          </template>
          <template slot="pageText" slot-scope="props">
            {{ $t('dataTables.pagingRow', { start: props.pageStart, stop: props.pageStop, length: props.itemsLength }) }}
          </template>
        </v-data-table>
      </v-card>
      <v-btn
        v-if="!printing && mappedDayProviders.length === 1"
        fab
        fixed
        right
        bottom
        color="primary"
        @click="print"
      >
        <v-icon>print</v-icon>
      </v-btn>
    </v-flex>
    <template v-else>
      <div class="center" :style="{color: 'rgba(0,0,0,.54)', 'height': '66vh'}">
        <div v-if="!$apollo.queries.providersDataFromDateRange.loading" class="center" :style="{top:0}">
          <v-icon :style="{'font-size': '13rem'}">lightbulb_outline</v-icon>
          <br>
          <div :style="{'font-size': '3rem'}"><span v-t="'ordersCountContainer.noProvidersHeader'" /></div>
          <div :style="{'font-size': '1.5rem'}"><span v-t="'ordersCountContainer.noProvidersBody'" /></div>
        </div>
        <v-progress-circular
          v-if="$apollo.queries.providersDataFromDateRange.loading"
          class="vertical-center"
          size="100"
          :width="6"
          indeterminate
          color="primary"
        />
      </div>
    </template>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import NotificationService from '@/services/NotificationService';
import ErrorHandler from '@/services/ErrorHandler';
import { mapGetters } from 'vuex';
import moment from 'moment';
import DatePicker from '../common/date-picker/DatePicker.vue';
import DayProviderDishesTable from './day-provider-dishes-table/DayProviderDishesTable.vue';
import TimeService from '@/services/TimeService';
import DialogWrapper from '../common/dialog-wrapper/DialogWrapper.vue';

const defaultPaginationDayProviders = {
  sortBy: 'date',
  descending: false,
  page: 1,
  rowsPerPage: 10,
};

export default {
  name: 'OrdersCountsContainer',
  components: {
    'date-picker': DatePicker,
    'day-provider-dishes-table': DayProviderDishesTable,
    'dialog-wrapper': DialogWrapper,
  },
  data() {
    return {
      loading: 0,
      providersDataFromDateRange: [],
      dayProvidersFromDateRangeWithPaging: {
        providers: [],
        totalCostPerDateRange: 0,
        allDayProvidersPaid: false,
        pagingData: {
          totalResultsCount: 0,
        },
      },
      dialog: false,
      selectedProviderId: undefined,
      selectedProviderToPay: undefined,
      selectedDate: {
        start: moment().format(TimeService.dateFormat),
        end: moment().format(TimeService.dateFormat),
      },
      selectedProviderData: undefined,
      includeFreeOfCharge: false,
      printing: false,
      dayProvidersPagination: defaultPaginationDayProviders,
    };
  },
  computed: {
    ...mapGetters('drawerStore', ['isDrawerOpen']),
    isMaxOrderUntilDateInPast() {
      return this.dayProvidersFromDateRangeWithPaging.maxOrderUntilDate === null
                || TimeService.IsDateInPast(this.dayProvidersFromDateRangeWithPaging.maxOrderUntilDate);
    },
    dayProviderHeaders() {
      return [
        {
          text: this.$t('ordersCountContainer.date'), value: 'date', align: 'left', sortable: true,
        },
        {
          text: this.$t('ordersCountContainer.total'), value: 'cost', align: 'right', sortable: false,
        },
        {
          text: this.$t('ordersCountContainer.paidCheckboxLabel'), value: 'paidStatus', align: 'right', sortable: false,
        },
      ];
    },
    rowsPerPage() {
      return [10, 20, 30];
    },
    mappedDayProviders() {
      return this.dayProvidersFromDateRangeWithPaging.providers.map(p => ({
        id: p.id,
        name: p.name,
        date: p.date,
        cost: p.cost,
        isFreeOfCharge: p.isFreeOfCharge,
        orderingTimeEnded: TimeService.IsDateInPast(p.orderUntil),
        paidStatus: p.paidStatus,
        paidStatusChangedByName: p.paidStatusChangedBy ? p.paidStatusChangedBy.displayName : undefined,
      }));
    },
    filteredProviderData() {
      const providerData = this.includeFreeOfCharge ? this.providersDataFromDateRange : this.providersDataFromDateRange.filter(p => p.notFreeOfChargeDaysCount > 0);

      return providerData;
    },
  },
  watch: {
    includeFreeOfCharge() {
      this.selectedProviderData = this.filteredProviderData[0] || undefined;
    },
    selectedProviderData() {
      this.resetDayProvidersPagination();
    },
  },
  mounted() {
    window.onbeforeprint = () => {
      this.printing = true;
    };
    window.onafterprint = () => {
      this.printing = false;
    };
  },
  methods: {
    resetDayProvidersPagination() {
      this.dayProvidersPagination = defaultPaginationDayProviders;
    },
    selectProvider(id) {
      if (this.selectedProviderId !== id) {
        this.selectedProviderId = id;
      }
    },
    onDateUpdate(date) {
      this.selectedDate = date;
    },
    openChangeProviderPaidStatusDialog(provider) {
      this.dialog = true;
      this.selectedProviderToPay = provider;
    },
    closeChangeProviderPaidStatusDialog() {
      this.dialog = false;
      this.selectedProviderToPay = undefined;
    },
    async confirmProviderPaidStatusChange(providerId, newPaidStatus) {
      this.loading = 1;
      await this.$apollo.mutate({
        mutation: gql`mutation ChangeProviderPaidStatusMutation ($paidStatus: Boolean!, $providerId: Int!) {
          changeProvidersPaidStatus(paidStatus: $paidStatus, providerId: $providerId) {
            paidStatus,
            paidStatusChangedBy {
              displayName
            }
          }
        }`,
        variables: {
          paidStatus: newPaidStatus,
          providerId,
        },
      })
        .then(({ data }) => {
          this.loading = 0;
          this.dayProvidersFromDateRangeWithPaging.providers
            .find(p => p.id === providerId).paidStatus = data.changeProvidersPaidStatus.paidStatus;

          this.dayProvidersFromDateRangeWithPaging.providers
            .find(p => p.id === providerId).paidStatusChangedBy = {
              displayName: data.changeProvidersPaidStatus.paidStatusChangedBy.displayName,
            };

          this.recalculateAllPaidCheckbox();
          this.closeChangeProviderPaidStatusDialog();
        })
        .catch(error => ErrorHandler.handleApolloError(error))
        .finally(() => { this.loading = 0; });
    },
    async bulkMarkAsPaid() {
      this.loading = 1;
      await this.$apollo.mutate({
        mutation: gql`mutation BulkMarkProvidersPaidMutation ($dateFrom: String!, $dateTo: String!, $providerDataId: Int!) {
          bulkMarkProvidersPaid(dateFrom: $dateFrom, dateTo: $dateTo, providerDataId: $providerDataId) {
            providerIds
            changedByUser {
                displayName
            }
          }
        }`,
        variables: {
          dateFrom: this.selectedDate.start,
          dateTo: this.selectedDate.end,
          providerDataId: this.selectedProviderData.id,
        },
      })
        .then(({ data }) => {
          data.bulkMarkProvidersPaid.providerIds.forEach((id) => {
            if (this.dayProvidersFromDateRangeWithPaging.providers.find(p => p.id === id)) {
              this.dayProvidersFromDateRangeWithPaging.providers
                .find(p => p.id === id).paidStatus = true;
              this.dayProvidersFromDateRangeWithPaging.providers
                .find(p => p.id === id).paidStatusChangedBy = {
                  displayName: data.bulkMarkProvidersPaid.changedByUser.displayName,
                };
            }
          });

          this.recalculateAllPaidCheckbox();
        })
        .catch(error => ErrorHandler.handleApolloError(error))
        .finally(() => { this.loading = 0; });
    },
    recalculateAllPaidCheckbox() {
      this.dayProvidersFromDateRangeWithPaging.allDayProvidersPaid = !this.dayProvidersFromDateRangeWithPaging.providers.some(p => !p.paidStatus && p.cost > 0);
    },
    print() {
      this.printing = true;
      setTimeout(() => window.print());
    },
    getDayOfWeek(date) {
      const dayOfWeek = moment(date).format('dddd').toLowerCase();

      return this.$t(`common.${dayOfWeek}`);
    },
    setSelectedProvider(providersDataFromDateRange) {
      if (this.includeFreeOfCharge) {
        this.selectedProviderData = providersDataFromDateRange[0] || undefined;
      } else {
        this.selectedProviderData = providersDataFromDateRange.filter(p => p.notFreeOfChargeDaysCount > 0)[0] || undefined;
      }
    },
  },
  apollo: {
    $loadingKey: 'loading',
    providersDataFromDateRange: {
      fetchPolicy: 'network-only',
      query: gql`query ProvidersDataFromDateRangeQuery($dateFrom: String!, $dateTo: String!) {
        providersDataFromDateRange(dateFrom: $dateFrom, dateTo: $dateTo) {
          id
          name
          freeOfChargeDaysCount
          notFreeOfChargeDaysCount
        }
      }`,
      variables() {
        return {
          dateFrom: this.selectedDate.start,
          dateTo: this.selectedDate.end,
        };
      },
      error(error) {
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          NotificationService.clearNetworkNotification();
          this.setSelectedProvider(r.data.providersDataFromDateRange);
        }
      },
    },
    dayProvidersFromDateRangeWithPaging: {
      fetchPolicy: 'no-cache',
      query: gql`query DayProvidersFromDateRangeQuery($dateFrom: String!, $dateTo: String!, $providerDataId: Int!, $includeFreeOfCharge: Boolean,
          $itemsPerPage: Int!, $offset: Int!, $sortField: String, $sortDirection: String) {
        dayProvidersFromDateRangeWithPaging(dateFrom: $dateFrom, dateTo: $dateTo, providerDataId: $providerDataId, includeFreeOfCharge: $includeFreeOfCharge,
          itemsPerPage: $itemsPerPage, offset: $offset,  sortField: $sortField, sortDirection: $sortDirection) {
          providers {
            id
            name
            date
            cost
            isFreeOfCharge
            orderUntil
            paidStatus
            paidStatusChangedBy {
              displayName
            }
          }
          totalCostPerDateRange
          allDayProvidersPaid
          maxOrderUntilDate
          pagingData {
            totalResultsCount
            resultsInPage
          }
        }
      }`,
      variables() {
        return {
          dateFrom: this.selectedDate.start,
          dateTo: this.selectedDate.end,
          providerDataId: this.selectedProviderData.id,
          includeFreeOfCharge: this.includeFreeOfCharge,
          itemsPerPage: this.dayProvidersPagination.rowsPerPage,
          offset: (this.dayProvidersPagination.page - 1) * this.dayProvidersPagination.rowsPerPage,
          sortField: this.dayProvidersPagination.sortBy,
          sortDirection: this.dayProvidersPagination.descending ? 'DESC' : 'ASC',
        };
      },
      skip() {
        return this.selectedProviderData === undefined;
      },
      error(error) {
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          NotificationService.clearNetworkNotification();
        }
      },
    },
  },
};
</script>


<style scoped>
  .paid-checkbox {
    float: right;
    margin-right: -10px;
    margin-bottom: -30px;
    margin-left: 5px;
    margin-top: -2px;
  }

  .all-paid-checkbox {
    margin: 0;
    display: flex;
    justify-content: end;
  }

  .orders-counts-toolbar {
    margin: auto;
    width: -webkit-max-content;
  }

  @media print {
    .orders-counts-table {
      flex-basis: 100% !important;
      max-width: 100% !important;
      padding: 0 !important;
    }

    .orders-counts-table >>> .card {
      border: 1px solid black;
    }

    .orders-counts-table >>> .card__title {
      border-bottom: 1px solid black;
      padding-top: 3px;
      padding-bottom: 3px;
    }

    .orders-counts-table >>> thead {
      display: none;
    }

    .orders-counts-table >>> td {
      height: 18px;
      font-size: small;
    }

    .orders-counts-table >>> tr:nth-child(even) {
      background-color: rgba(204, 204, 204, 0.25);
      -webkit-print-color-adjust: exact;
    }

    .printable-dish-name {
      border: black;
      border-width: 1px;
      border-style: solid;
      background-color: white;
      text-align: center;
      margin: 1px;
      padding: 4px 16px;
      font-size: 30px;
    }

    .pagebreak {
      page-break-before: always;
    }

    .print-display-none {
      display: none;
    }
  }
</style>
